// ============================================================================
// Módulo CROSS "tour" — motor de COACH-MARKS de Lealify (sin librerías).
// Port del motor del onboarding de ForoBeta (mockups/onboarding) a React:
//  - overlay oscuro + "hueco" resaltado (spotlight con box-shadow gigante)
//  - popover con dots + Saltar/Siguiente, clamp dentro del viewport
//  - scrollIntoView ANTES de medir (gotcha ForoBeta) y SALTA pasos cuyo
//    target no exista o no sea visible (getClientRects) — nunca apunta a nada
//  - pasos sin target = tarjeta centrada (splash / avisos / acciones)
//  - tema: consume las vars CSS del host con fallback claro (ver README)
// Portable: esta carpeta no importa NADA de la app (solo react/react-dom).
// ============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TourStep, TourFarewell } from './types';
import { markTourSeen } from './api';

interface TourProps {
  tourKey: string;
  steps: TourStep[];
  open: boolean;
  /** Se llama SIEMPRE al cerrar (terminado o saltado); el visto ya quedó marcado. */
  onClose: () => void;
  /** Paso inicial al abrir (para reanudar tras pausar por un overlay del host). */
  startAt?: number;
  /** Despedida integrada: tarjeta centrada final con un solo botón (sin "Saltar"). */
  farewell?: TourFarewell;
}

const PAD = 8;        // aire alrededor del target resaltado
const MARGIN = 12;    // margen mínimo del popover contra el viewport
const POP_W = 340;    // ancho máximo del popover

interface SpotRect { top: number; left: number; width: number; height: number }

const css = `
.lt-spot { position: fixed; z-index: 9998; border-radius: 12px; pointer-events: none;
  box-shadow: 0 0 0 9999px var(--lt-dim, rgba(15,18,25,.62)); transition: all .25s ease; }
.lt-dim-full { position: fixed; inset: 0; z-index: 9998; background: var(--lt-dim, rgba(15,18,25,.62)); }
.lt-blocker { position: fixed; inset: 0; z-index: 9999; }
.lt-pop { position: fixed; z-index: 10000; width: min(${POP_W}px, calc(100vw - ${MARGIN * 2}px));
  background: var(--surface-elevated, #fff); color: var(--content-primary, #111827);
  border: 1px solid var(--border-default, #e5e7eb); border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0,0,0,.28); padding: 16px; transition: top .25s ease, left .25s ease; }
.lt-pop--center { left: 50%; top: 50%; transform: translate(-50%, -50%); }
.lt-emoji { font-size: 26px; line-height: 1; margin-bottom: 8px; }
.lt-title { font-weight: 700; font-size: 15px; margin: 0 0 6px; }
.lt-body { font-size: 13.5px; line-height: 1.55; color: var(--content-secondary, #4b5563); margin: 0; }
.lt-foot { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; gap: 8px; }
.lt-dots { display: flex; gap: 5px; }
.lt-dot { width: 7px; height: 7px; border-radius: 999px; background: var(--border-default, #e5e7eb); }
.lt-dot--on { background: var(--primary, #0891b2); }
.lt-btn { border: 0; border-radius: 9px; padding: 8px 14px; font-size: 13px; font-weight: 600;
  cursor: pointer; background: var(--primary, #0891b2); color: #fff; }
.lt-btn:active { transform: scale(.97); }
.lt-btn--ghost { background: transparent; color: var(--content-muted, #6b7280); font-weight: 500; }
.lt-foot--bye { justify-content: center; }
.lt-btn--wide { width: 100%; padding: 10px 14px; font-size: 14px; }
.lt-action { display: block; width: 100%; margin-top: 12px; text-align: center;
  border: 0; border-radius: 10px; padding: 10px 14px; font-size: 14px; font-weight: 700;
  cursor: pointer; background: var(--primary, #0891b2); color: #fff; }
`;

const isVisible = (el: Element | null): el is HTMLElement =>
  !!el && (el as HTMLElement).getClientRects().length > 0;

// Primer elemento VISIBLE que matchee. Los selectores separados por coma se
// evalúan EN ORDEN (prioridad), no en orden de DOM: así un paso puede declarar
// 'target-desktop, target-móvil' y cada layout resalta lo suyo. Gotcha heredado
// del onboarding ForoBeta (targets de otro breakpoint existen pero display:none).
const findVisible = (selector: string): HTMLElement | null => {
  for (const part of selector.split(',')) {
    const sel = part.trim();
    if (!sel) continue;
    for (const el of Array.from(document.querySelectorAll(sel))) {
      if (isVisible(el)) return el;
    }
  }
  return null;
};

const Tour: React.FC<TourProps> = ({ tourKey, steps: baseSteps, open, onClose, startAt, farewell }) => {
  // La despedida se integra como último paso centrado (marca __farewell para el footer).
  const steps = useMemo<(TourStep & { __farewell?: boolean })[]>(
    () => (farewell
      ? [...baseSteps, { emoji: farewell.emoji ?? '🎉', title: farewell.title, body: farewell.body, __farewell: true }]
      : baseSteps),
    [baseSteps, farewell]
  );
  const [idx, setIdx] = useState(0);
  const [spot, setSpot] = useState<SpotRect | null>(null);
  const [popPos, setPopPos] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const lastStepRef = useRef(0);

  const step = steps[idx];

  // Primer paso VISIBLE desde `from` (salta targets inexistentes/ocultos). -1 = no hay.
  const nextVisible = useCallback((from: number, dir: 1 | -1 = 1): number => {
    for (let i = from; i >= 0 && i < steps.length; i += dir) {
      const s = steps[i];
      if (!s.target || findVisible(s.target)) return i;
    }
    return -1;
  }, [steps]);

  const finish = useCallback((completed: boolean) => {
    steps[idx]?.onLeave?.();
    markTourSeen(tourKey, { completed, lastStep: lastStepRef.current + 1 });
    onClose();
  }, [tourKey, steps, idx, onClose]);

  const goTo = useCallback((target: number, dir: 1 | -1 = 1) => {
    steps[idx]?.onLeave?.();
    const next = nextVisible(target, dir);
    if (next === -1) { finish(true); return; }
    setIdx(next);
  }, [idx, steps, nextVisible, finish]);

  // Al abrir: arrancar en el primer paso visible desde startAt (0 = inicio).
  useEffect(() => {
    if (!open) return;
    setIdx((prev) => { void prev; const first = nextVisible(startAt || 0); return first === -1 ? 0 : first; });
    setSpot(null); setPopPos(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Posicionar el paso actual (gotcha ForoBeta: scrollIntoView ANTES de medir + clamp).
  useEffect(() => {
    if (!open || !step) return;
    lastStepRef.current = idx;
    let cancelled = false;
    step.onEnter?.();

    const position = () => {
      if (cancelled) return;
      if (!step.target) { setSpot(null); setPopPos(null); return; }
      const el = findVisible(step.target);
      if (!el) { setSpot(null); setPopPos(null); return; }
      el.scrollIntoView({ block: 'center', inline: 'nearest' });
      requestAnimationFrame(() => {
        if (cancelled) return;
        const r = el.getBoundingClientRect();
        setSpot({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
        const popH = popRef.current?.offsetHeight || 190;
        const below = r.bottom + PAD + 10;
        const top = below + popH + MARGIN <= window.innerHeight
          ? below
          : Math.max(MARGIN, r.top - PAD - 10 - popH);
        const left = Math.min(
          Math.max(MARGIN, r.left + r.width / 2 - POP_W / 2),
          window.innerWidth - Math.min(POP_W, window.innerWidth - MARGIN * 2) - MARGIN
        );
        setPopPos({ top, left });
      });
    };

    const t = window.setTimeout(position, step.enterDelayMs || 60);
    window.addEventListener('resize', position);
    return () => { cancelled = true; window.clearTimeout(t); window.removeEventListener('resize', position); };
  }, [open, idx, step]);

  if (!open || !step) return null;

  const isLast = nextVisible(idx + 1) === -1;
  const centered = !step.target || !spot;

  return createPortal(
    <div className="lealify-tour">
      <style>{css}</style>
      {centered
        ? <div className="lt-dim-full" />
        : <div className="lt-spot" style={{ top: spot!.top, left: spot!.left, width: spot!.width, height: spot!.height }} />}
      <div className="lt-blocker" />
      <div
        ref={popRef}
        className={`lt-pop${centered ? ' lt-pop--center' : ''}`}
        style={centered ? undefined : { top: popPos?.top ?? -9999, left: popPos?.left ?? -9999 }}
        role="dialog"
        aria-label={step.title}
      >
        {step.emoji && <div className="lt-emoji">{step.emoji}</div>}
        <p className="lt-title">{step.title}</p>
        <p className="lt-body">{step.body}</p>
        {step.actionLabel && (
          <button className="lt-action" onClick={step.onAction}>{step.actionLabel}</button>
        )}
        {step.__farewell ? (
          <div className="lt-foot lt-foot--bye">
            <button className="lt-btn lt-btn--wide" onClick={() => finish(true)}>
              {farewell?.ctaLabel || '¡Listo!'}
            </button>
          </div>
        ) : (
          <div className="lt-foot">
            <button className="lt-btn lt-btn--ghost" onClick={() => finish(false)}>Saltar</button>
            <div className="lt-dots">
              {steps.map((_, i) => <span key={i} className={`lt-dot${i === idx ? ' lt-dot--on' : ''}`} />)}
            </div>
            <button className="lt-btn" onClick={() => (isLast ? finish(true) : goTo(idx + 1))}>
              {isLast ? 'Terminar' : 'Siguiente'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Tour;
